import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProject, updateProject, uploadDeliverable, createTimeEntry, deleteTimeEntry, assignDeliverableInvoice, deleteDeliverable, downloadFile, getProjectDownloads } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { SectionRule } from '@/components/ui/section-rule';
import { StatusDot } from '@/components/ui/status-dot';
import { EmptyState } from '@/components/ui/empty-state';
import { Metric } from '@/components/ui/metric';
import { Field } from '@/components/ui/field';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, Loader2, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

/* Below 640px the tables collapse to labelled stacked rows. */
const ROW = 'block py-2 sm:table-row sm:py-0';
const CELL = 'flex h-auto items-baseline justify-between gap-4 px-0 py-1 before:text-micro before:uppercase before:text-ink-subtle before:content-[attr(data-label)] sm:table-cell sm:h-11 sm:px-3 sm:py-0 sm:before:content-none';

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showAddTime, setShowAddTime] = useState(false);
  const [timeForm, setTimeForm] = useState({ description: '', hours: '', rate: '', date: new Date().toISOString().split('T')[0] });
  const [savingTime, setSavingTime] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [downloads, setDownloads] = useState([]);
  const [deliverableFilter, setDeliverableFilter] = useState('pending');
  const [timeEntryFilter, setTimeEntryFilter] = useState('pending');
  const dropRef = useRef(null);
  const navigate = useNavigate();

  const fetchProject = useCallback(() => {
    getProject(id).then(r => { setProject(r.data); setLoading(false); }).catch(() => { setLoading(false); navigate('/projects'); });
  }, [id, navigate]);

  const fetchDownloads = useCallback(() => {
    getProjectDownloads(id).then(r => setDownloads(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => { fetchProject(); }, [fetchProject]);
  useEffect(() => { fetchDownloads(); }, [fetchDownloads]);

  const handleAssignMilestone = async (deliverableId, value) => {
    const invoiceId = value === 'none' ? null : value;
    try {
      await assignDeliverableInvoice(id, deliverableId, invoiceId);
      toast.success(invoiceId ? 'Milestone set' : 'Milestone cleared');
      fetchProject();
    } catch {
      toast.error('Failed to update milestone');
    }
  };

  const doUpload = async (file) => {
    setUploading(true);
    setUploadProgress(10);
    try {
      const interval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 15, 90));
      }, 300);
      await uploadDeliverable(id, file);
      clearInterval(interval);
      setUploadProgress(100);
      toast.success(`${file.name} uploaded successfully`);
      setTimeout(() => { setUploadProgress(0); setUploading(false); fetchProject(); }, 500);
    } catch {
      toast.error('Upload failed');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await doUpload(file);
    e.target.value = '';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await doUpload(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };

  const handleRename = async () => {
    if (!renameValue.trim()) { toast.error('Project name is required'); return; }
    setRenaming(true);
    try {
      const res = await updateProject(id, { name: renameValue.trim() });
      setProject(prev => ({ ...prev, ...res.data }));
      toast.success('Project renamed');
      setShowRename(false);
    } catch { toast.error('Failed to rename project'); }
    setRenaming(false);
  };

  const handleAddTime = async () => {
    if (!timeForm.description.trim() || !timeForm.hours) { toast.error('Description and hours required'); return; }
    setSavingTime(true);
    try {
      await createTimeEntry(id, {
        description: timeForm.description,
        hours: parseFloat(timeForm.hours),
        rate: timeForm.rate ? parseFloat(timeForm.rate) : 0,
        date: timeForm.date || undefined
      });
      toast.success('Time entry added');
      setShowAddTime(false);
      setTimeForm({ description: '', hours: '', rate: '', date: new Date().toISOString().split('T')[0] });
      fetchProject();
    } catch { toast.error('Failed to add time entry'); }
    setSavingTime(false);
  };

  const handleDeleteTime = async (entryId) => {
    try {
      await deleteTimeEntry(id, entryId);
      toast.success('Time entry deleted');
      fetchProject();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeleteDeliverable = async (deliverable) => {
    if (!window.confirm(`Delete "${deliverable.file_name}"? This cannot be undone.`)) return;
    try {
      await deleteDeliverable(id, deliverable.id);
      toast.success('Deliverable deleted');
      fetchProject();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete deliverable');
    }
  };

  const handleDownloadDeliverable = async (deliverable) => {
    try {
      const res = await downloadFile(deliverable.file_path);
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = deliverable.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (loading) return <div className="h-64 animate-pulse rounded-md bg-wash" />;
  if (!project) return null;

  const dropzoneTone = isDragOver ? 'border-forest bg-forest-tint' : 'border-line-strong';

  const filteredDeliverables = (project.deliverables || []).filter(d => {
    if (deliverableFilter === 'pending') return !d.invoice_id;
    if (deliverableFilter === 'invoiced') return !!d.invoice_id;
    return true;
  });
  const filteredTimeEntries = (project.time_entries || []).filter(e => {
    if (timeEntryFilter === 'pending') return !e.invoiced;
    if (timeEntryFilter === 'invoiced') return !!e.invoiced;
    return true;
  });

  return (
    <div data-testid="project-detail-page">
      <button
        onClick={() => navigate('/projects')}
        className="mb-4 inline-flex items-center gap-1.5 text-small text-ink-muted transition-colors hover:text-forest"
      >
        <ArrowLeft strokeWidth={1.5} className="h-3.5 w-3.5" /> Projects
      </button>

      <PageHeader
        title={project.name}
        description={project.client?.name}
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setRenameValue(project.name || ''); setShowRename(true); }}
              data-testid="rename-project-btn"
            >
              Rename
            </Button>
            <Select value={project.status} disabled={updatingStatus} onValueChange={async (val) => {
              setUpdatingStatus(true);
              try {
                const res = await updateProject(id, { status: val });
                setProject(prev => ({ ...prev, ...res.data }));
                toast.success(`Project marked as ${val}`);
              } catch { toast.error('Failed to update status'); }
              setUpdatingStatus(false);
            }}>
              <SelectTrigger className="h-8 w-[130px]" aria-label="Project status" data-testid="project-status-select">
                {updatingStatus ? <Loader2 strokeWidth={1.5} className="h-3.5 w-3.5 animate-spin" /> : <SelectValue />}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => navigate(`/invoices/new?project=${id}&client=${project.client_id}`)}
              data-testid="generate-invoice-btn"
            >
              Generate invoice
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
        <Metric label="Hours logged" value={`${project.total_hours || 0}h`} />
        <Metric label="Deliverables" value={project.deliverables?.length || 0} />
        <Metric label="Invoices" value={project.invoices?.length || 0} />
      </div>

      <section className="mt-10">
        <SectionRule
          label="Deliverables"
          action={
            <label className="cursor-pointer">
              <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="file-upload-input" />
              <Button asChild size="sm" variant="outline" disabled={uploading} data-testid="upload-btn">
                <span>{uploading ? <><Loader2 strokeWidth={1.5} className="animate-spin" /> Uploading…</> : 'Upload file'}</span>
              </Button>
            </label>
          }
        />

        {!project.deliverables?.length ? (
          <div
            ref={dropRef}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`rounded-md border border-dashed py-16 text-center transition-colors ${dropzoneTone}`}
            data-testid="dropzone"
          >
            <p className="font-display text-subhead text-ink">
              {isDragOver ? 'Drop to upload' : 'No deliverables yet'}
            </p>
            <p className="mt-2 text-small text-ink-muted">Drag files here, or use Upload file above.</p>
            {uploading && (
              <div className="mx-auto mt-4 max-w-xs">
                <Progress value={uploadProgress} className="h-1" />
                <p className="mt-2 text-small tabular-nums text-ink-muted">{uploadProgress}%</p>
              </div>
            )}
          </div>
        ) : (
          <>
            <div
              ref={dropRef}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`mb-4 rounded-md border border-dashed p-4 text-center transition-colors ${dropzoneTone}`}
            >
              <p className="text-small text-ink-muted">{isDragOver ? 'Drop to upload' : 'Drag & drop more files here'}</p>
              {uploading && (
                <div className="mx-auto mt-3 max-w-xs">
                  <Progress value={uploadProgress} className="h-1" />
                  <p className="mt-2 text-small tabular-nums text-ink-muted">{uploadProgress}%</p>
                </div>
              )}
            </div>

            <Tabs value={deliverableFilter} onValueChange={setDeliverableFilter} className="mb-4">
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="pending">Pending</TabsTrigger>
                <TabsTrigger value="invoiced">Invoiced</TabsTrigger>
              </TabsList>
            </Tabs>

            {filteredDeliverables.length === 0 ? (
              <EmptyState
                title={`No ${deliverableFilter} deliverables`}
                description={deliverableFilter === 'invoiced'
                  ? "Files show up here once they're gated to a specific invoice."
                  : 'Files show up here until they get gated to a specific invoice.'}
              />
            ) : (
              <Table>
                <TableHeader className="hidden sm:table-header-group">
                  <TableRow>
                    <TableHead>File</TableHead>
                    <TableHead>Access</TableHead>
                    <TableHead>Unlocks with</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                    <TableHead className="w-20"><span className="sr-only">Actions</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDeliverables.map(d => (
                    <TableRow key={d.id} className={ROW} data-testid={`deliverable-${d.id}`}>
                      <TableCell data-label="File" className={`${CELL} text-ink`}>{d.file_name}</TableCell>
                      <TableCell data-label="Access" className={CELL}>
                        <StatusDot status={d.is_locked ? 'locked' : 'unlocked'} />
                      </TableCell>
                      <TableCell data-label="Unlocks with" className={CELL}>
                        {d.is_locked ? (
                          <Select
                            value={d.invoice_id || 'none'}
                            onValueChange={(v) => handleAssignMilestone(d.id, v)}
                          >
                            <SelectTrigger
                              className="h-8 w-[190px]"
                              aria-label={`Milestone gating ${d.file_name}`}
                              data-testid={`milestone-select-${d.id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Any payment</SelectItem>
                              {(project.invoices || []).map(inv => (
                                <SelectItem key={inv.id} value={inv.id}>
                                  {inv.milestone_label || `Invoice #${inv.invoice_number}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-small text-ink-subtle">Released</span>
                        )}
                      </TableCell>
                      <TableCell data-label="Size" className={`${CELL} text-ink-muted sm:text-right`}>{formatFileSize(d.file_size)}</TableCell>
                      <TableCell className={`${CELL} before:content-none sm:text-right`}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-ink-subtle hover:text-ink"
                          onClick={() => handleDownloadDeliverable(d)}
                          aria-label={`Download ${d.file_name}`}
                          data-testid={`download-deliverable-${d.id}`}
                        >
                          <Download strokeWidth={1.5} />
                        </Button>
                        {!d.invoice_id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-ink-subtle hover:text-destructive"
                            onClick={() => handleDeleteDeliverable(d)}
                            aria-label={`Delete ${d.file_name}`}
                            data-testid={`delete-deliverable-${d.id}`}
                          >
                            <Trash2 strokeWidth={1.5} />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </section>

      <section className="mt-10" data-testid="time-tracking-card">
        <SectionRule
          label={`Time tracking (${project.total_hours || 0}h total)`}
          action={
            <Button onClick={() => setShowAddTime(true)} size="sm" variant="outline" data-testid="add-time-btn">
              Log time
            </Button>
          }
        />
        {!project.time_entries?.length ? (
          <EmptyState
            title="No time entries yet"
            description="Track your hours to generate accurate invoices."
          />
        ) : (
          <>
            <Tabs value={timeEntryFilter} onValueChange={setTimeEntryFilter} className="mb-4">
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="pending">Pending</TabsTrigger>
                <TabsTrigger value="invoiced">Invoiced</TabsTrigger>
              </TabsList>
            </Tabs>

            {filteredTimeEntries.length === 0 ? (
              <EmptyState
                title={`No ${timeEntryFilter} time entries`}
                description={timeEntryFilter === 'invoiced'
                  ? 'Entries show up here once they have been billed on an invoice.'
                  : 'Entries show up here until they get billed on an invoice.'}
              />
            ) : (
              <Table>
                <TableHeader className="hidden sm:table-header-group">
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="w-10"><span className="sr-only">Actions</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTimeEntries.map(e => (
                    <TableRow key={e.id} className={ROW} data-testid={`time-entry-${e.id}`}>
                      <TableCell data-label="Description" className={`${CELL} text-ink`}>{e.description}</TableCell>
                      <TableCell data-label="Date" className={`${CELL} text-ink-muted`}>{e.date}</TableCell>
                      <TableCell data-label="Hours" className={`${CELL} text-ink sm:text-right`}>{e.hours}</TableCell>
                      <TableCell data-label="Rate" className={`${CELL} text-ink-muted sm:text-right`}>
                        {e.rate > 0 ? `${formatCurrency(e.rate)}/hr` : '—'}
                      </TableCell>
                      <TableCell data-label="Amount" className={`${CELL} text-ink sm:text-right`}>
                        {e.rate > 0 ? formatCurrency(e.amount) : '—'}
                      </TableCell>
                      <TableCell className={`${CELL} before:content-none sm:text-right`}>
                        {!e.invoiced && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-ink-subtle hover:text-destructive"
                            onClick={() => handleDeleteTime(e.id)}
                            aria-label={`Delete time entry: ${e.description}`}
                          >
                            <Trash2 strokeWidth={1.5} />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </section>

      <section className="mt-10" data-testid="download-activity">
        <SectionRule
          label="Download activity"
          action={
            <span className="text-small tabular-nums text-ink-subtle">
              {downloads.length} client download{downloads.length === 1 ? '' : 's'}
            </span>
          }
        />
        {downloads.length === 0 ? (
          <EmptyState
            title="No client downloads yet"
            description="Once a client fetches an unlocked file, the receipt appears here."
          />
        ) : (
          <Table>
            <TableHeader className="hidden sm:table-header-group">
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>When</TableHead>
                <TableHead className="text-right">IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {downloads.map(dl => (
                <TableRow key={dl.id} className={ROW}>
                  <TableCell data-label="File" className={`${CELL} text-ink`}>{dl.file_name}</TableCell>
                  <TableCell data-label="When" className={`${CELL} text-ink-muted`}>
                    {dl.downloaded_at ? new Date(dl.downloaded_at).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell data-label="IP" className={`${CELL} text-ink-subtle sm:text-right`}>{dl.ip || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      {project.invoices?.length > 0 && (
        <section className="mt-10">
          <SectionRule label="Invoices" />
          <Table>
            <TableHeader className="hidden sm:table-header-group">
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {project.invoices.map(inv => (
                <TableRow key={inv.id} className={ROW}>
                  <TableCell data-label="Invoice" className={`${CELL} text-ink`}>#{inv.invoice_number}</TableCell>
                  <TableCell data-label="Status" className={CELL}><StatusDot status={inv.status} /></TableCell>
                  <TableCell data-label="Amount" className={`${CELL} text-ink sm:text-right`}>{formatCurrency(inv.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      )}

      <Dialog open={showRename} onOpenChange={setShowRename}>
        <DialogContent className="sm:max-w-md" data-testid="rename-project-dialog">
          <DialogHeader>
            <DialogTitle>Rename project</DialogTitle>
            <DialogDescription>
              New projects inherit the accepted proposal's title. Rename it to anything you like.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Project name" htmlFor="rename-project-input" hint="Required">
              <Input
                id="rename-project-input"
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRename(); }}
                data-testid="rename-project-input"
              />
            </Field>
            <div className="flex justify-end gap-3 border-t border-line pt-4">
              <Button variant="ghost" onClick={() => setShowRename(false)}>Cancel</Button>
              <Button onClick={handleRename} disabled={renaming} data-testid="save-rename-btn">
                {renaming ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddTime} onOpenChange={setShowAddTime}>
        <DialogContent className="sm:max-w-md" data-testid="add-time-dialog">
          <DialogHeader>
            <DialogTitle>Log time</DialogTitle>
            <DialogDescription>Track hours worked on this project.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Description" htmlFor="time-desc" hint="Required">
              <Input id="time-desc" value={timeForm.description} onChange={e => setTimeForm({ ...timeForm, description: e.target.value })} placeholder="What did you work on?" data-testid="time-desc-input" />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Hours" htmlFor="time-hours">
                <Input id="time-hours" type="number" step="0.25" value={timeForm.hours} onChange={e => setTimeForm({ ...timeForm, hours: e.target.value })} placeholder="2.5" className="tabular-nums" data-testid="time-hours-input" />
              </Field>
              <Field label="Rate/hr" htmlFor="time-rate">
                <Input id="time-rate" type="number" value={timeForm.rate} onChange={e => setTimeForm({ ...timeForm, rate: e.target.value })} placeholder="100" className="tabular-nums" data-testid="time-rate-input" />
              </Field>
              <Field label="Date" htmlFor="time-date">
                <Input id="time-date" type="date" value={timeForm.date} onChange={e => setTimeForm({ ...timeForm, date: e.target.value })} className="tabular-nums" data-testid="time-date-input" />
              </Field>
            </div>
            <div className="flex justify-end gap-3 border-t border-line pt-4">
              <Button variant="ghost" onClick={() => setShowAddTime(false)}>Cancel</Button>
              <Button onClick={handleAddTime} disabled={savingTime} data-testid="save-time-btn">
                {savingTime ? 'Saving…' : 'Log time'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
